import { apiService } from './apiService';

export const documentService = {
  // Get all documents
  async getDocuments(params?: { 
    entityType?: string; 
    entityId?: string; 
    entityIds?: string;
    documentType?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.entityType) queryParams.append('entityType', params.entityType);
    if (params?.entityId) queryParams.append('entityId', params.entityId);
    if (params?.entityIds) queryParams.append('entityIds', params.entityIds);
    if (params?.documentType) queryParams.append('documentType', params.documentType);
    
    const query = queryParams.toString();
    return await apiService.get(`/documents${query ? `?${query}` : ''}`);
  },

  // Get document by ID
  async getDocument(id: string | number) {
    return await apiService.get(`/documents/${id}`);
  },

  // Download all documents of a type as a zip
  async downloadZip(params: { entityType: string; documentType: string; entityIds?: string }): Promise<{ blob: Blob; fileName: string }> {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('authToken');

    const response = await fetch(`${baseUrl}/documents/zip`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      let message = 'Zip oluşturulurken hata oluştu';
      try {
        const data = await response.json();
        if (data?.message) message = data.message;
      } catch {
        // ignore parse errors
      }
      throw new Error(message);
    }

    const disposition = String(response.headers.get('content-disposition') || '');
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const fileName = match ? match[1] : 'belgeler.zip';

    return { blob: await response.blob(), fileName };
  },

  // Upload document
  async uploadDocument(formData: FormData) {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('authToken');

    const response = await fetch(`${baseUrl}/documents/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: formData,
    });

    const rawBody = await response.text();
    let data: any = {};
    if (rawBody) {
      try {
        data = JSON.parse(rawBody);
      } catch {
        if (!response.ok) {
          const compactBody = rawBody.replace(/\s+/g, ' ').slice(0, 300);
          throw new Error(`Belge yuklenemedi: sunucu beklenmeyen bir yanit dondurdu (${compactBody})`);
        }
      }
    }

    if (!response.ok) {
      throw new Error(data.message || 'Belge yuklenemedi');
    }

    return data;
  },

  // Update document
  async updateDocument(id: string | number, data: any) {
    return await apiService.put(`/documents/${id}`, data);
  },

  // Delete document
  async deleteDocument(id: string | number) {
    return await apiService.delete(`/documents/${id}`);
  },

  // OCR Tax Plate
  async ocrTaxPlate(file: File): Promise<{
    success: boolean;
    data?: {
      invoiceTitle: string | null;
      taxOffice: string | null;
      taxNumber: string | null;
      confidence: number;
      rawText: string;
    };
    message?: string;
  }> {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('authToken');

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${baseUrl}/ocr/tax-plate`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: formData,
    });

    const data = await response.json();
    return data;
  },

  async ocrInvoiceCode(file: File): Promise<{
    success: boolean;
    data?: {
      invoiceCode: string | null;
      confidence: number;
      rawText: string;
    };
    message?: string;
  }> {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('authToken');

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${baseUrl}/ocr/invoice-code`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: formData,
    });

    return response.json();
  },

  async ocrReceipt(file: File): Promise<{
    success: boolean;
    data?: {
      merchant: string | null;
      total: number | null;
      date: string | null;
      taxNumber: string | null;
      items?: Array<{ description: string; amount: number; raw: string }>;
      suggestedCategory?: string;
      categoryConfidence?: number;
      summary?: {
        subtotal?: number | null;
        vat?: number | null;
        grandTotal?: number | null;
      };
      lines?: string[];
      confidence: number;
      rawText: string;
    };
    message?: string;
  }> {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('authToken');

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${baseUrl}/ocr/receipt`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: formData,
    });

    return response.json();
  },
};
